<template>
    <edit-resource
        :title="title"
        :form="form"
        @close="$emit('close')"
        @submit="saveCompetence"
    >
        <div class="w-600">
            <div>
                <ul class="leading-none flex flex-wrap justify-end">
                    <li
                        v-for="tag in form.tags"
                        :key="tag.id"
                        class="flex-none bg-primary-80 text-white font-semibold px-8 h-24 flex items-center rounded-6 mr-4 mb-8"
                    >{{ tag.title }}</li>
                    <li class="flex-none">
                        <a @click="startAddingTag" class="pl-4 h-24 flex items-center font-semibold rounded-6 inline-block text-primary-40 trans-ease hover:text-primary-80 cursor-pointer">
                            <svg-icon type="add-circle" class="max-w-16 max-h-16 mr-4"></svg-icon>
                            <span>{{ form.tags.length ? $t('applications.manage_tags') : $t('applications.add_tags') }}</span>
                        </a>
                    </li>
                </ul>

                <tag-selector
                    v-if="updatingTags"
                    @close="stopAddingTag"
                    :resource-title="form.title[locale]"
                    :current-tags="form.tags"
                    @update="updateTags"
                ></tag-selector>
            </div>

            <translatable #default="{ languages, selectedLanguage }" :errors="warningTabs">
                <div v-for="lang in languages" :key="lang">

                    <simple-input class="mb-16" v-model="form.title[lang]" v-show="lang === selectedLanguage">
                        {{ $t('settings.email-templates.name') }} <span v-if="languages.length > 1">({{ lang }})</span>
                    </simple-input>

                    <simple-textarea
                        v-show="lang === selectedLanguage"
                        v-model="form.description[lang]"
                        full-featured
                    >
                        {{ $t('form.description') }} <span v-if="languages.length > 1">({{ lang }})</span>
                    </simple-textarea>

                </div>
            </translatable>
        </div>
    </edit-resource>
</template>

<script>
import EditResource from './EditResource.vue';
import Input from '../forms/Input.vue';
import Form from '../../etc/forms/Form';
import Translatable from '../forms/Translatable.vue';
import * as competences from '../../services/CompetenceService';
import { normalizeJson } from '../../../shared/helpers';
import TextArea from '../forms/TextArea.vue';
import Competence from '../../models/Competence';
import Job from '../../models/Job';
import JobCompetence from '../../models/JobCompetence';
import SvgIcon from './SvgIcon.vue';
import TagSelector from './TagSelector.vue';
import CompetenceTag from '../../models/CompetenceTag';
import _xorBy from 'lodash/xorBy';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';

function getFields () {
    return [
        { name: 'title', value: {}, translatable: true },
        { name: 'description', value: {}, translatable: true },
        { name: 'tags', value: [] },
    ];
}

export default {
    components: {
        'edit-resource': EditResource,
        'simple-input': Input,
        'simple-textarea': TextArea,
        'translatable': Translatable,
        'tag-selector': TagSelector,
        'svg-icon': SvgIcon,
    },
    props: {
        competence: Competence,
        // If a job is passed, the created competence will automatically be attached to the job.
        job: Job
    },
    data: function () {
        return {
            form: this.generateForm(),
            updatingTags: false,
        }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'locale']),
        title () {
            return this.$t(this.competence ? 'general.edit_subject' : 'general.add_subject', { subject: this.$t('applications.competence') })
        },
        warningTabs() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.title[languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
    },
    methods: {
        generateForm () {
            let fields = getFields();

            if (this.competence) {
                fields.forEach((field, index) => {
                    fields[index].value = JSON.parse(JSON.stringify(this.competence[field.name]));
                });
            }

            return new Form(fields);
        },

        async saveCompetence () {
            if (this.competence && this.competence.id) {
                await this.updateCompetence();
            } else {
                await this.createCompetence();
            }

            this.$emit('close');
        },
        updateCompetence () {
            return this.form.submit(data => {
                data = this.formatBeforeSave(data, _xorBy([...this.form.tags], this.competence.tags, 'id').length > 0);

                return competences.update(this.competence.id, data, ['tags']);
            }).then(result => {
                // First drop relations : Update doesn't update relations
                useRepo(CompetenceTag).where('competenece_id', this.competence.id).delete();
                return useRepo(Competence).save(normalizeJson(result));
            });
        },
        createCompetence () {
            let creationProcess = this.form.submit(data => {
                data = this.formatBeforeSave(data, data.tags.length > 0)

                return competences.store(data, ['tags']);
            }).then(result => {
                return useRepo(Competence).save(normalizeJson(result));
            });

            if (!this.job) {
                return creationProcess;
            }

            return creationProcess
                .then(inserts => {
                    return competences.storeForJob(this.job.id, { data: {
                        id: inserts.competences[0].id,
                        pivot: {
                            weight: 'default',
                        }
                    }})
                })
                .then(result => {
                    return useRepo(JobCompetence).save(normalizeJson(result));
                });
        },
        updateTags (tags) {
            this.form.tags = tags;
            this.updatingTags = false;
        },
        startAddingTag () {
            this.updatingTags = true;
        },
        stopAddingTag () {
            this.updatingTags = false;
        },
        formatBeforeSave (data, addTags = false) {
            let body = {
                data: { attributes: {}},
            };

            // First check if there are actually changes...
            if(addTags) {
                body.data.relationships = { tags: {
                    data: [...data.tags].map(tag => {
                        return { type: 'tags', id: tag.id }
                    })
                }};
            }

            delete data.tags;
            body.data.attributes = {...data};

            return body;
        }
    },
}
</script>
